import React, { useState, useCallback } from 'react';
import { Trans } from '@lingui/react/macro';
import { Modal, ModalHeader, ModalBody, SimpleWizard, SimpleWizardStep } from '@ygtechnologies/bygr-frontend-component-library';
import DocumentUpload from '../DocumentUpload';
import SuccessScreen from '@/components/SuccessScreen';
import { ResidentialUnitDocument } from '@/types';
import DocumentUploadWizardNewVersion from './DocumentUploadWizardNewVersion';
import { Button } from '@ygtechnologies/rubus-ui';
import { triggerHandler } from '@ygtechnologies/rubus-frontend-utilities';
import { LargeRadio } from '@/components/forms/Radio';

enum Step {
    Intro,
    DocumentSelect,
    Upload,
    Success,
}

const Steps = [Step.Intro, Step.DocumentSelect, Step.Upload, Step.Success];
const StepsNormalUpload = [Step.Intro, Step.Upload, Step.Success];

type Props = {
    residentialUnitId?: number;
    isOpen: boolean;
    disableDrop?: boolean;
    document: ResidentialUnitDocument;
    projectId: number;
    onClose?: () => void;
    onSave: () => void;
};

const DocumentUploadWizardDialog: React.FC<Props> = ({
    isOpen,
    residentialUnitId,
    onSave,
    onClose,
    disableDrop,
    projectId,
    document: initialDocument,
}) => {
    const [isNewVersionPath, setIsNewVersionPath] = useState(false);
    const [step, setStep] = useState(initialDocument ? Step.Upload : Step.Intro);
    const [key, setKey] = useState(1);
    const [document, setDocument] = useState<ResidentialUnitDocument | null>(initialDocument);

    const reset = useCallback(() => {
        setIsNewVersionPath(false);
        setStep(Step.Intro);
        setDocument(initialDocument);
    }, [initialDocument]);

    const handleSubmit = () => {};
    const handleDocumentSave = useCallback(() => {
        onSave();
        setStep(Step.Success);
    }, [onSave]);

    const handleStepChange = useCallback((step: Step) => {
        setStep(step);
        if (step === Step.Intro) {
            setDocument(null);
        }
    }, []);

    const onOpen = useCallback(() => {
        reset();
        setKey((v) => v + 1);
    }, [reset]);

    const handleClose = useCallback(() => {
        triggerHandler(onClose);
    }, [onClose]);

    return (
        <Modal onClose={reset} size="lg" toggle={handleClose} isOpen={isOpen} onOpen={onOpen}>
            <ModalHeader>
                <Trans>Upload document</Trans>
            </ModalHeader>

            <ModalBody>
                <SimpleWizard
                    onCancel={handleClose}
                    steps={isNewVersionPath ? Steps : StepsNormalUpload}
                    currentStep={step}
                    onStepChange={handleStepChange}
                    onSubmit={handleSubmit}
                    // saveButtonProps={saveButtonProps}
                >
                    <SimpleWizardStep stepNumber={Step.Intro} header={<Trans>What would you like to do?</Trans>}>
                        <div className="flex column">
                            <LargeRadio onChange={() => setIsNewVersionPath(false)} checked={!isNewVersionPath}>
                                <strong>
                                    <Trans>Upload a new document</Trans>
                                </strong>
                                <Trans>Select and upload a new document from your computer.</Trans>
                            </LargeRadio>

                            <LargeRadio onChange={() => setIsNewVersionPath(true)} checked={isNewVersionPath}>
                                <strong>
                                    <Trans>Upload a new version of an existing document</Trans>
                                </strong>
                                <Trans>Select and upload a new version from your computer.</Trans>
                            </LargeRadio>
                        </div>
                    </SimpleWizardStep>

                    <SimpleWizardStep
                        onBack={reset}
                        stepNumber={Step.DocumentSelect}
                        nextButton={
                            document?.documentId
                                ? undefined
                                : () => (
                                      <Button variant="primary" disabled>
                                          <Trans>Next</Trans>
                                      </Button>
                                  )
                        }
                        header={<Trans>Upload a new version of an existing document </Trans>}>
                        <DocumentUploadWizardNewVersion
                            onSelect={(document) => setDocument(document)}
                            selectedDocumentId={document?.documentId ?? null}
                            residentialUnitId={residentialUnitId}
                        />
                    </SimpleWizardStep>

                    <SimpleWizardStep hideButtons stepNumber={Step.Upload} header={<Trans>Upload a new document</Trans>}>
                        <DocumentUpload
                            closeOnUpload={false}
                            key={key}
                            onClose={handleClose}
                            document={document}
                            projectId={projectId}
                            disableDrop={disableDrop}
                            onSave={handleDocumentSave}
                            residentialUnitId={residentialUnitId}
                            onBack={isNewVersionPath ? () => setStep(Step.DocumentSelect) : reset}
                        />
                    </SimpleWizardStep>

                    <SimpleWizardStep hideButtons stepNumber={Step.Success} header={<Trans>Document uploaded</Trans>}>
                        <SuccessScreen text={<Trans>Document uploaded successfully!</Trans>} />
                        <div className="flex end">
                            <Button variant="primary" onClick={handleClose}>
                                <Trans>Back to documents</Trans>
                            </Button>
                        </div>
                    </SimpleWizardStep>
                </SimpleWizard>
            </ModalBody>
        </Modal>
    );
};

export default DocumentUploadWizardDialog;
